import { CalendarOff, ChevronRight, Search, TerminalSquare } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { CopyButton } from "@/client/components/copy-button";
import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/client/components/ui/card";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { type Node, nodesApi } from "@/client/features/nodes/nodes-api";
import {
  ALL_RACKS,
  type RackOption,
  rackKeyOf,
  rackOptionLabel,
  rackOptionsFrom,
} from "@/client/features/nodes/rack-filter";
import { HoldDialog } from "@/client/features/maintenance/holds-section";
import { ApiError } from "@/client/lib/api";

// Map a NetBox device status to a badge tone. Unknown statuses fall back to a
// neutral outline so new NetBox statuses still render sensibly.
function statusVariant(status: string | null): "secondary" | "outline" | "destructive" | "warning" {
  switch (status) {
    case "production":
    case "active":
      return "secondary";
    case "maintenance":
      return "warning";
    case "offline":
    case "failed":
    case "decommissioning":
      return "destructive";
    default:
      return "outline";
  }
}

// Free-text search over the fields that are effectively unique per node (IPs and
// hardware serial), so a single box can pinpoint a machine. Rack is deliberately
// excluded here - racks group many nodes, so they get a dedicated dropdown filter.
function matchesQuery(n: Node, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  const internal = n.internalIp?.toLowerCase() ?? "";
  const publicIp = n.publicIpAddress?.toLowerCase() ?? "";
  const serial = n.serial?.toLowerCase() ?? "";
  return internal.includes(needle) || publicIp.includes(needle) || serial.includes(needle);
}

// Native select styled to match the shadcn inputs used elsewhere (mirrors the
// staff allocation picker), so this page doesn't need an extra UI primitive.
const rackSelectClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

// A monospace IP plus a copy button (PE-939) that reveals on row hover/focus so
// the column stays uncluttered. The wrapper stops click propagation so copying
// never triggers the row's navigate-to-node handler.
function CopyableIp({ ip }: { ip: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className="font-mono text-xs break-all">{ip}</span>
      <span
        className="opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <CopyButton value={ip} label="Copy IP" className="size-6" />
      </span>
    </span>
  );
}

/**
 * Public-IP cell: the assigned address from NetBox (PE-933), otherwise nothing.
 * The node list shows only the IP (PE-931) - the ports live on the node detail
 * page, not this column.
 */
function PublicIpCell({ ip }: { ip: string | null }) {
  if (!ip) {
    return <span className="text-muted-foreground">—</span>;
  }
  return <CopyableIp ip={ip} />;
}

function NodesTable({
  nodes,
  selected,
  onToggle,
  onToggleAll,
  onOpen,
  onOpenConsole,
  showConsole,
}: {
  nodes: Node[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onOpen: (id: string) => void;
  onOpenConsole: (id: string) => void;
  /** Show the per-row Console & power shortcut (BMC feature enabled). */
  showConsole: boolean;
}) {
  const allSelected = nodes.length > 0 && nodes.every((n) => selected.has(n.id));
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 sticky top-0">
          <tr className="text-muted-foreground border-b text-left">
            <th className="w-10 py-2 pr-2 pl-3">
              <input
                type="checkbox"
                className="size-4 align-middle"
                aria-label="Select all nodes"
                checked={allSelected}
                onChange={onToggleAll}
              />
            </th>
            <th className="py-2 pr-4 font-medium">Internal IP</th>
            <th className="py-2 pr-4 font-medium">Public IP</th>
            <th className="py-2 pr-4 font-medium">GPU type</th>
            <th className="py-2 pr-4 font-medium">Serial</th>
            <th className="py-2 pr-4 font-medium">Status</th>
            <th className="py-2 pr-4 font-medium">Site</th>
            <th className="py-2 pr-4 font-medium">Rack</th>
            <th className="w-32 py-2 pr-3" />
          </tr>
        </thead>
        <tbody>
          {nodes.map((n) => {
            const isSelected = selected.has(n.id);
            return (
              <tr
                key={n.id}
                data-selected={isSelected}
                onClick={() => onOpen(n.id)}
                className="group hover:bg-muted/40 data-[selected=true]:bg-muted/60 cursor-pointer border-b transition-colors last:border-0"
              >
                <td className="py-2 pr-2 pl-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="size-4 align-middle"
                    aria-label={`Select ${n.internalIp ?? "node"}`}
                    checked={isSelected}
                    onChange={() => onToggle(n.id)}
                  />
                </td>
                <td className="py-2 pr-4">
                  {n.internalIp ? (
                    <CopyableIp ip={n.internalIp} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-2 pr-4">
                  <PublicIpCell ip={n.publicIpAddress} />
                </td>
                <td className="py-2 pr-4">{n.gpuType ?? "—"}</td>
                <td className="text-muted-foreground py-2 pr-4 font-mono text-xs break-all">
                  {n.serial ?? "—"}
                </td>
                <td className="py-2 pr-4">
                  <Badge variant={statusVariant(n.status)}>
                    {n.statusLabel ?? n.status ?? "—"}
                  </Badge>
                </td>
                <td className="text-muted-foreground py-2 pr-4">{n.site ?? "—"}</td>
                <td className="text-muted-foreground py-2 pr-4">{n.rack ?? "—"}</td>
                <td className="py-2 pr-3">
                  <div className="flex items-center justify-end gap-2">
                    {showConsole ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 px-2.5 text-xs"
                        title="Console & power"
                        aria-label={`Open console and power for ${n.internalIp ?? "node"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenConsole(n.id);
                        }}
                      >
                        <TerminalSquare className="size-3.5" />
                        Console
                      </Button>
                    ) : null}
                    <ChevronRight className="text-muted-foreground size-4 shrink-0" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function NodesPage() {
  const navigate = useNavigate();
  const bmcEnabled = useAuthStore((s) => s.features.bmc);
  const deactivated = useAuthStore((s) => s.deactivated);
  const [nodes, setNodes] = useState<Node[] | null>(null);
  const [configured, setConfigured] = useState(true);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [rackFilter, setRackFilter] = useState<string>(ALL_RACKS);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    try {
      const res = await nodesApi.list();
      setNodes(res.nodes);
      setConfigured(res.netboxConfigured);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load nodes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Distinct racks present in the current node set, each with a count, so the
  // dropdown only ever offers racks the tenant actually has hardware in.
  const rackOptions = useMemo<RackOption[]>(() => rackOptionsFrom(nodes ?? []), [nodes]);

  // Reset the rack filter if a resync removes the rack it pointed at.
  useEffect(() => {
    if (rackFilter === ALL_RACKS) return;
    if (!rackOptions.some((r) => r.key === rackFilter)) setRackFilter(ALL_RACKS);
  }, [rackOptions, rackFilter]);

  const filtered = useMemo(
    () =>
      (nodes ?? []).filter(
        (n) => matchesQuery(n, query) && (rackFilter === ALL_RACKS || rackKeyOf(n) === rackFilter),
      ),
    [nodes, query, rackFilter],
  );

  // Keep selection valid if the underlying list changes after a resync.
  useEffect(() => {
    if (!nodes) return;
    const ids = new Set(nodes.map((n) => n.id));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [nodes]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllShown = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      const allShown = filtered.length > 0 && filtered.every((n) => next.has(n.id));
      if (allShown) for (const n of filtered) next.delete(n.id);
      else for (const n of filtered) next.add(n.id);
      return next;
    });

  const selectedNodes = (nodes ?? []).filter((n) => selected.has(n.id));
  // Identify nodes by IP only - the Mantle node name/hostname are never exposed
  // to customers (PE-914). Nodes without an IP contribute nothing to the prefill.
  const scopePrefill = selectedNodes
    .map((n) => n.internalIp)
    .filter((ip): ip is string => Boolean(ip))
    .join(", ");
  const allocatedTitle = nodes === null ? "Nodes Allocated" : `Nodes Allocated: ${nodes.length}`;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Nodes" description="The compute nodes allocated to your organization." />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>{allocatedTitle}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : error ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load nodes. Refresh to try again.
            </p>
          ) : !configured ? (
            <p className="text-muted-foreground text-sm">
              Node visibility isn't available for your organization yet. Contact Mantle if you
              expect to see allocated hardware here.
            </p>
          ) : nodes && nodes.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative max-w-xs flex-1">
                  <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Filter by IP or serial"
                    className="pl-8"
                    aria-label="Filter nodes by internal IP, public IP, or serial"
                  />
                </div>
                <select
                  aria-label="Filter by rack"
                  className={rackSelectClass}
                  value={rackFilter}
                  onChange={(e) => setRackFilter(e.target.value)}
                >
                  <option value={ALL_RACKS}>All racks ({(nodes ?? []).length})</option>
                  {rackOptions.map((r) => (
                    <option key={r.key} value={r.key}>
                      {rackOptionLabel(r)} ({r.count})
                    </option>
                  ))}
                </select>
                <div className="bg-muted/60 ml-auto flex items-center gap-2 rounded-md px-2 py-1 text-sm">
                  <span className="text-muted-foreground">
                    {selected.size > 0 ? `${selected.size} selected` : "Select nodes"}
                  </span>
                  <HoldDialog
                    onSaved={() => setSelected(new Set())}
                    initialValues={{ affectedScope: scopePrefill }}
                    trigger={
                      <Button size="sm" disabled={selected.size === 0 || deactivated}>
                        <CalendarOff className="size-3.5" />
                        Request no-disruption window
                      </Button>
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelected(new Set())}
                    disabled={selected.size === 0}
                  >
                    Clear
                  </Button>
                </div>
              </div>
              {filtered.length > 0 ? (
                <NodesTable
                  nodes={filtered}
                  selected={selected}
                  onToggle={toggle}
                  onToggleAll={toggleAllShown}
                  onOpen={(id) => navigate(`/nodes/${id}`)}
                  onOpenConsole={(id) => navigate(`/nodes/${id}/console`)}
                  showConsole={bmcEnabled}
                />
              ) : (
                <p className="text-muted-foreground text-sm">No nodes match the current filters.</p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              No nodes are allocated to your organization yet. They'll appear here once Mantle
              assigns them.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
